import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import {
  FC,
  ForwardRefExoticComponent,
  JSX,
  PropsWithoutRef,
  RefAttributes,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useSubmitDraftsMutation, useUpdatePiecesMutation } from '@api/piece';
import {
  CaptureMethod,
  Piece,
  PieceAction,
  PieceAnnotation,
  PieceAssetSize,
  PieceAssetType,
  PieceType,
  Status,
  Workflow,
} from '@apiContract';
import AutomatedCapture from '@components/Capture/components/AutomatedCapture';
import DeviceCamera from '@components/Capture/components/DeviceCamera';
import DropdownMenuActions from '@components/Capture/components/DropdownMenuActions';
import RetryIconWithLoader from '@components/Capture/components/IconWithLoader/components/RetryIconWithLoader';
import { BarcodeLocalIcon } from '@components/Capture/icons/BarcodeLocalIcon';
import { LocationIcon } from '@components/Capture/icons/LocationIcon';
import { PersonPinRoundedIcon } from '@components/Capture/icons/PersonPinRoundedIcon';
import { Rotate90DegreesIcon } from '@components/Capture/icons/Rotate90DegreesIcon';
import AdditionalInfo from '@components/DrawerContent/AdditionalInfo';
import AssignmentList from '@components/DrawerContent/AssignmentList';
import Barcode from '@components/DrawerContent/Barcode';
import LocationID from '@components/DrawerContent/LocationID';
import { ICropperRef } from '@components/ImageCropper';
import { ImageEditor } from '@components/ImageEditor';
import Loading from '@components/PercentLoading';
import { useMe } from '@hooks/useMe';
import { usePieceAnnotations } from '@hooks/usePieceAnnotation';
import { useSettings } from '@hooks/useSettings';
import { Options } from '@rdTypes/capture';
import isNumber from 'lodash/fp/isNumber';
import keys from 'lodash/fp/keys';
import last from 'lodash/fp/last';
import pickBy from 'lodash/fp/pickBy';
import { receivedDigitalApi } from 'src/redux/api';
import { dispatch as storeDispatch } from 'src/redux/store';
import { v4 } from 'uuid';

import { postStartCapturing, postStopCapturing } from '@src/api/automatedCapture';
import { rotateImage } from '@utils/image';
import { getPieceAsset, isPieceSend } from '@utils/piece';
import { getCompanyFromHost } from '@utils/url';

import CameraPhoto from '../components/CameraPhoto';
import Comments from '../components/Comments';
import NotFound from '../components/NotFound';
import PieceSlider from '../components/PieceSlider';
import ReceiveConfig from '../components/ReceiveConfig';
import Review from '../components/Review';
import UploadImages from '../components/UploadImages';
import {
  getButtonConfig,
  getFinishCaptureNavigateProps,
  nextStepReceiveConfig,
  parseUploadFiles,
  toBase64,
  tooltips,
  waitCameraForIdleState,
} from '../helper';
import { PrintIcon } from '../icons/PrintIcon';
import {
  ButtonConfig,
  CaptureId,
  DrawerTypes,
  IdV4,
  OnPieceCreateType,
  Steps,
  UploadRef,
} from '../types';
import { ActionTypes, FileOptionPayload, initStore, reducer } from './reducer';
import { useReceiveSession } from './useReceiveSession';
import { useReceiveSessionItems } from './useReceiveSessionItems';
import { useUpdateQueue } from './useUpdateQueue';
import { useUploadWorker } from './useUploadWorker';
import { useCaptureWebSockets } from './useWebsockets';
import { useRDInstance } from 'src/hooks/useRdInstance';

type ComponentByState = {
  Component:
    | FC<any>
    | ((props: any) => JSX.Element | null)
    | ForwardRefExoticComponent<PropsWithoutRef<unknown> & RefAttributes<unknown>>
    | null;
  props?: object;
  onClose?: VoidFunction;
};

type DrawerComponent = {
  Component: ((props: any) => JSX.Element) | null;
  props: Record<string, unknown> & { onClose: () => void };
};

export const useCapture = (optionData: Options) => {
  const [params, setParams] = useSearchParams();
  const paramSessionId = params.get('sessionId');
  const { currentSessionProps, receiveSessionError, getSessionIdOrCreate } = useReceiveSession(
    !paramSessionId || paramSessionId === 'new' ? undefined : paramSessionId,
  );
  const [submitDrafts] = useSubmitDraftsMutation();
  const settings: Record<string, boolean> = useSettings(['LabelPrintingEnabled', 'AutomatedCaptureEnabled']);

  const [
    { files, cursor, captureState, drawer, receiveSession, cropReturnPath, uploadError, options, isLoading },
    dispatch,
  ] = useReducer(reducer, initStore(optionData, !paramSessionId || paramSessionId === 'new'));
  const navigate = useNavigate();
  const { currentUser } = useMe();

  const currentFile = cursor ? files[cursor] : null;

  const autoPrint =
    currentSessionProps.receiveSession?.autoPrintLabelOnAssignment ?? options.autoPrintLabelOnAssignment;

  const [, forceUpdate] = useState({});
  const [automatedCaptureInProgress, setAutomatedCaptureInProgress] = useState(false);
  const { myConnectedInstance } = useRDInstance();
  const setCaptureState = useCallback((nextCaptureState: Steps) => {
    dispatch({ type: ActionTypes.SET_CAPTURE_STATE, payload: nextCaptureState });
  }, []);

  usePieceAnnotations(
    (annotations: PieceAnnotation[]) => dispatch({ type: ActionTypes.SET_PIECE_ANNOTATIONS, payload: annotations }),
    isNumber(cursor) ? cursor : null,
  );

  const lastPiece = last(Object.values(files));

  const totalItemsToLoad = currentSessionProps.receiveSession
    ? +currentSessionProps.receiveSession.totalPiecesCount + currentSessionProps.receiveSession.draftPiecesCount
    : 0;

  if (
    captureState === Steps.Loading &&
    (receiveSessionError || (currentSessionProps && currentSessionProps.receiveSession && !totalItemsToLoad))
  ) {
    setCaptureState(Steps.NotFound);
  }

  const getFilteredFilesByStatus = (status: Status, addEmpty: boolean = false) => {
    return pickBy((item) => item.piece?.status === status || (addEmpty && !item.piece), files);
  };

  const hasDraftStatusFiles = keys(getFilteredFilesByStatus(Status.Draft)).length > 0;

  if (
    captureState === Steps.Loading &&
    totalItemsToLoad &&
    Math.round((100 / totalItemsToLoad) * Object.keys(files).length) >= 100
  ) {
    setTimeout(() => {
      const captureType = params.get('captureType') as Steps;

      const determineCaptureState = () => {
        if (captureType && [Steps.Photo, Steps.Images].includes(captureType)) {
          return captureType;
        }

        return hasDraftStatusFiles ? Steps.AutomatedCapture : Steps.ReviewItems;
      };

      if (paramSessionId) {
        dispatch({ type: ActionTypes.SET_RECEIVE_SESSION, payload: paramSessionId });
      }
      setCaptureState(determineCaptureState());
    }, 300);
  }

  useReceiveSessionItems(
    currentSessionProps,
    (items: Piece[]) => {
      dispatch({ type: ActionTypes.ADD_PIECE_LOAD_LIST, payload: items });
    },
    dispatch,
  );

  const setCursor = useCallback(
    (nextCursor?: CaptureId | null) => dispatch({ type: ActionTypes.SET_CURSOR, payload: nextCursor || null }),
    [],
  );

  const onDeletePiece = async (pieceId: number) => {
    if (cursor === pieceId) {
      const items = Object.values(files);
      let prevCursorIndex = items.findIndex(({ id }) => id === pieceId);
      prevCursorIndex = prevCursorIndex > 0 ? prevCursorIndex - 1 : prevCursorIndex + 1;
      setCursor(items.length > 1 ? items[prevCursorIndex].id : null);
    }

    dispatch({ type: ActionTypes.REMOVE_FILE, payload: pieceId });
  };

  const onRemoveDrafts = () => {
    dispatch({ type: ActionTypes.REMOVE_DRAFTS, payload: null });
  };

  const setDrawer = useCallback((nextDrawer: DrawerTypes | null) => {
    dispatch({ type: ActionTypes.SET_DRAWER, payload: nextDrawer });
  }, []);

  const setFileOption = useCallback((payload: FileOptionPayload) => {
    dispatch({ type: ActionTypes.SET_FILE_OPTION, payload });
  }, []);

  const cropperRef = useRef<ICropperRef | null | undefined>(null);
  const uploadImagesRef = useRef<UploadRef>(null);
  const [updatePieces] = useUpdatePiecesMutation();

  const { addEvent } = useUpdateQueue({ context: 'capture' });

  useEffect(
    () => () => {
      storeDispatch(
        receivedDigitalApi.util.invalidateTags(['RS', 'Pieces', 'Containers', 'Container', 'PiecesCounters', 'Piece']),
      );
    },
    [],
  );

  useCaptureWebSockets(dispatch, setFileOption, options, addEvent, receiveSession);

  const onPieceCreated = ({ fileId, piece, status }: OnPieceCreateType) => {
    setFileOption({ id: fileId, option: 'status', value: status });
    if (piece) {
      setFileOption({ id: fileId, option: 'piece', value: piece });
      dispatch({ type: ActionTypes.UPDATE_FILE_ID, payload: fileId });
    }
  };

  const { addFile } = useUploadWorker(onPieceCreated);

  const handlePhotoClick = useCallback(
    async (photo: string) => {
      const sessionId = await getSessionIdOrCreate(options);

      const id = v4();

      if (sessionId && photo) {
        dispatch({ type: ActionTypes.INIT_FILE, payload: { id, photo, status: 'queued' } });
        dispatch({ type: ActionTypes.SET_RECEIVE_SESSION, payload: sessionId });
        setFileOption({ id, option: 'status', value: 'queued' });
        addFile({ file: photo, sessionId, fileId: id as string, host: getCompanyFromHost(), resize: true });
      }

      if (autoPrint) {
        setCursor(id);
        setCaptureState(Steps.SliderPrint);
      }
    },
    [addFile, getSessionIdOrCreate, options, setCaptureState, setCursor, setFileOption, autoPrint],
  );

  const handleStartAutomatedCapture = async (cameraIndex: number) => {
    setAutomatedCaptureInProgress(true);
    const sessionId = await getSessionIdOrCreate(options);

    if (sessionId) {
      dispatch({ type: ActionTypes.SET_RECEIVE_SESSION, payload: sessionId });

      try {

        if (cameraIndex !== undefined && myConnectedInstance) {
          await postStartCapturing({
            rdConnectInstanceId: myConnectedInstance.id as number,
            receiveSessionId: sessionId as string,
            index: cameraIndex,
          });
        }
      } catch (error) {
        console.error('Error starting capturing:', error);
      }

      await waitCameraForIdleState({ rdConnectInstanceId: myConnectedInstance?.id as number }, setAutomatedCaptureInProgress);
    }
  };

  const handleStopAutomatedCapture = async () => {
    try {
      await postStopCapturing({ rdConnectInstanceId: myConnectedInstance?.id as number });
    } catch (error) {
      console.error('Error stopping capturing:', error);
    } finally {
      setAutomatedCaptureInProgress(false);
    }
  };

  const handleSwitchAutomatedCamera = () => {
    setCaptureState(Steps.AutomatedCaptureCamera);
  };

  const handleErrorStartReceive = () => {
    params.delete('sessionId');
    params.delete('captureType');
    params.append('sessionId', 'new');
    setParams(params);
    setCaptureState(Steps.ReceiveConfig);
  };

  const closeDrawer = () => setDrawer(null);

  const handleReviewItems = () => setCaptureState(Steps.ReviewItems);

  const handleSubmitDrafts = () => {
    submitDrafts({ receiveSessionId: paramSessionId as string })
      .unwrap()
      .then((res) => {
        dispatch({ type: ActionTypes.CREATE_PIECES_FROM_DRAFTS, payload: res.objectData });
      });
    setCaptureState(Steps.ReviewItems);
  };

  const onUpload = useCallback(
    async (uploadedFiles: Array<File | string>) => {
      const sessionId = await getSessionIdOrCreate(options);

      if (sessionId) {
        const data = parseUploadFiles(uploadedFiles, sessionId);
        data.forEach(({ file, fileId }) => {
          if (file) {
            dispatch({ type: ActionTypes.INIT_FILE, payload: { photo: file, id: fileId } });
          }
        });
        addFile(data);

        dispatch({ type: ActionTypes.SET_RECEIVE_SESSION, payload: sessionId });
        setCaptureState(Steps.ReviewItems);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addFile, receiveSession, options],
  );

  const handleCrop = async (returnPath: Steps = Steps.Slider) => {
    if (!cursor) return;
    let edit = files[cursor].photo;
    if (!edit && files[cursor].piece) {
      const largeAsset = getPieceAsset(
        files[cursor].piece?.pieceAssets || [],
        PieceAssetSize.Large,
        PieceAssetType.Original,
      );

      if (largeAsset?.url) {
        try {
          const response = await fetch(largeAsset.url);
          const blob = await response.blob();
          edit = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Failed to load image:', error);
          return;
        }
      }
    } else if (files[cursor].photo && typeof files[cursor].photo !== 'string') {
      edit = (await toBase64(files[cursor].photo as File)) as string;
    }
    setFileOption({ id: cursor, option: 'prevImage', value: files[cursor].editImage });
    setFileOption({ id: cursor, option: 'editImage', value: edit });
    dispatch({ type: ActionTypes.SET_CROP_RETURN_PATH, payload: returnPath });
    setCaptureState(Steps.Crop);
  };

  const handleRotate = () => {
    if (!cursor) return;
    rotateImage(currentFile?.editImage || '', -90, (result) => {
      setFileOption({ id: cursor, option: 'editImage', value: result });
    });
  };

  const retryFailedPieces = async (id?: IdV4) => {
    const sessionId = await getSessionIdOrCreate(options);
    const failedPieces = Object.values(files).filter((item) => item.status === 'failed');

    if (failedPieces.length && sessionId) {
      const failedFiles = id
        ? { file: files[id].photo, sessionId, fileId: id, host: getCompanyFromHost() }
        : failedPieces.map((item) => ({
            file: item.photo,
            sessionId,
            fileId: item.id as IdV4,
            host: getCompanyFromHost(),
          }));

      addFile(failedFiles);
      dispatch({ type: ActionTypes.SET_RECEIVE_SESSION, payload: sessionId });
      if (id) {
        setFileOption({ id, option: 'status', value: 'queued' });
      } else {
        failedPieces.forEach(({ id: piceId }) => setFileOption({ id: piceId, option: 'status', value: 'queued' }));
      }
    }
  };

  const updateReceiveConfig = (payload: Partial<Options>) => {
    dispatch({ type: ActionTypes.UPDATE_RECEIVE_CONFIG, payload });
  };

  const setIsLoading = (payload: boolean) => {
    dispatch({ type: ActionTypes.SET_IS_LOADING, payload });
  };

  const isUnresolvedPieces = () => {
    return Object.values(files).some((item) => {
      const { piece } = item;
      return !piece || !piece.recipient;
    });
  };

  const isUploadingPieces = () => {
    return Object.values(files).some(
      (item) => !['failed', 'detected', 'completed', 'loadedFromServer'].includes(item.status),
    );
  };

  const onStartReceive = () => {
    if (options.captureMethod) {
      if (options.captureMethod !== CaptureMethod.Single || options.pieceType !== PieceType.Package) {
        updateReceiveConfig({ autoPrintLabelOnAssignment: false });
      }
      setCaptureState(nextStepReceiveConfig[options.captureMethod]);
    }
  };

  const onNextState = () => {
    if (captureState === Steps.ReviewItems) {
      setCaptureState(Steps.Photo);
    }
  };

  const getButtonsByState = (onClose: () => boolean | void): ButtonConfig[] => {
    switch (true) {
      case captureState === Steps.ReceiveConfig:
        return [getButtonConfig('Start Receiving', 'primary', onStartReceive, isLoading)];
      case captureState === Steps.Photo && keys(files).length > 0:
        return [getButtonConfig('Review Items', 'primary', handleReviewItems)];
      case captureState === Steps.Slider:
      case captureState === Steps.SliderPrint:
        return [
          getButtonConfig('Review Items', 'inherit', handleReviewItems),
          getButtonConfig('Continue', 'primary', () => {
            setCaptureState(Steps.Photo);
            setCursor(null);
          }),
        ];
      case captureState === Steps.Crop:
        return [
          getButtonConfig('Cancel', 'inherit', () => {
            if (cursor) {
              setFileOption({ id: cursor, option: 'prevImage', value: null });
              setFileOption({ id: cursor, option: 'editImage', value: files[cursor].prevImage });
            }
            if (cropReturnPath) {
              setCaptureState(cropReturnPath);
            }
          }),
          getButtonConfig('Save Changes', 'primary', async () => {
            const cropResult = await cropperRef.current?.done();
            if (!cropResult) return;
            const { img, cropPoints } = cropResult;
            if (cropReturnPath) {
              setCaptureState(cropReturnPath);
            }
            if (cursor) {
              const result = await updatePieces({
                action: PieceAction.CropImage,
                pieces: [
                  {
                    id: +cursor,
                    croppedFileBase64: img.split(',')[1],
                    croppingBox: {
                      topLeftX: Math.floor(cropPoints.topLeft.x),
                      topLeftY: Math.floor(cropPoints.topLeft.y),
                      topRightX: Math.floor(cropPoints.topRight.x),
                      topRightY: Math.floor(cropPoints.topRight.y),
                      bottomLeftX: Math.floor(cropPoints.bottomLeft.x),
                      bottomLeftY: Math.floor(cropPoints.bottomLeft.y),
                      bottomRightX: Math.floor(cropPoints.bottomRight.x),
                      bottomRightY: Math.floor(cropPoints.bottomRight.y),
                    },
                  },
                ],
                context: 'capture',
              }).unwrap();
              setFileOption({ id: cursor, option: 'piece', value: result[0] });
              setFileOption({ id: cursor, option: 'editImage', value: null });
            }
          }),
        ];
      case captureState === Steps.ReviewItems:
        return [
          getButtonConfig(
            'Continue Receiving',
            'inherit',
            setCaptureState,
            !currentSessionProps.isNew &&
              currentSessionProps.receiveSession &&
              currentSessionProps.receiveSession.isClosed,
            { captureDropdown: true },
          ),
          getButtonConfig('Finish', 'primary', () => {
            const isFinished = onClose();
            if (isFinished) {
              navigate(getFinishCaptureNavigateProps(currentUser?.id.toString() ?? ''));
            }
          }),
        ];
      case captureState === Steps.Images:
        return [
          ...(Object.values(files).length ? [getButtonConfig('Review Items', 'inherit', handleReviewItems)] : []),
          getButtonConfig(
            'Upload Images',
            'primary',
            uploadImagesRef.current?.upload ?? (() => {}),
            !uploadImagesRef.current || uploadError,
          ),
        ];
      case captureState === Steps.AutomatedCapture:
        return [
          getButtonConfig('Review Items', 'inherit', handleReviewItems),
          getButtonConfig(
            'Create Items',
            'primary',
            handleSubmitDrafts,
            !hasDraftStatusFiles || automatedCaptureInProgress,
          ),
        ];
      case captureState === Steps.AutomatedCaptureCamera:
        return [getButtonConfig('Use This Camera', 'primary', () => setCaptureState(Steps.AutomatedCapture))];
      default:
        return [];
    }
  };

  const getIconsByState = (): JSX.Element[] | [] => {
    const isSent = isPieceSend(currentFile?.piece);

    switch (true) {
      case [Steps.Slider, Steps.SliderPrint].includes(captureState): {
         if (isSent) {
          return [];
        }
        const icons = [
          <PersonPinRoundedIcon
            key={tooltips.PersonPinRounded}
            capturePiece={currentFile}
            onClick={() => setDrawer(DrawerTypes.User)}
          />,
        ];

        if (
          currentSessionProps.receiveSession?.workflow &&
          ![Workflow.HighDensityMail, Workflow.MailBox, Workflow.Lockers].includes(
            currentSessionProps.receiveSession?.workflow,
          )
        ) {
          icons.splice(
            1,
            0,
            <LocationIcon
              key={tooltips.LocationOnRounded}
              capturePiece={currentFile}
              onClick={() => setDrawer(DrawerTypes.Location)}
            />,
          );
        }

        icons.splice(
          1,
          0,
          <BarcodeLocalIcon
            key="BarcodeLocalIcon"
            capturePiece={currentFile}
            workflow={options.workflow}
            onClick={() => setDrawer(DrawerTypes.Barcode)}
          />,
        );

        if (settings.LabelPrintingEnabled) {
          icons.push(<PrintIcon key="printIcon" piece={currentFile?.piece} />);
        }

        icons.push(
          <DropdownMenuActions
            key="DropdownMenuActions"
            capturePiece={currentFile}
            setDrawer={setDrawer}
            onDeletePiece={onDeletePiece}
            onCropClick={() => handleCrop(Steps.Slider)}
            currentSession={currentSessionProps}
          />,
        );

        return icons;
      }
      case captureState === Steps.Crop:
        return [<Rotate90DegreesIcon key={tooltips.Rotate90Degrees} onClick={handleRotate} />];
      case captureState === Steps.ReviewItems: {
        const isUploading = isUploadingPieces();
        const failedPieces = Object.values(files).filter((item) => item.status === 'failed');
        return failedPieces.length || isUploading
          ? [
              <RetryIconWithLoader
                key={tooltips.CloudUploadRounded}
                Icon={CloudUploadRounded}
                onClick={retryFailedPieces}
                isLoading={isUploading}
                retryContent={failedPieces.length}
              />,
            ]
          : [];
      }
      default:
        return [];
    }
  };

  const getTitleByState = () => {
    switch (captureState) {
      case Steps.ReceiveConfig:
        return 'Receive Items';
      case Steps.Photo:
        return 'Capture';
      case Steps.Slider:
      case Steps.SliderPrint:
      case Steps.Crop:
        return 'Edit';
      case Steps.Images:
        return 'Upload Images';
      case Steps.ReviewItems:
      case Steps.NotFound:
        return 'Review Items';
      case Steps.AutomatedCapture:
        return 'Automated Capture';
      case Steps.AutomatedCaptureCamera:
        return 'Camera Preview';
      default:
        return '';
    }
  };

  const getComponentByState = (): ComponentByState => {
    switch (captureState) {
      case Steps.Loading:
        return {
          Component: Loading,
          props: {
            percent: Math.round(
              (100 / (currentSessionProps.receiveSession?.totalPiecesCount || 1)) *
                Object.keys(getFilteredFilesByStatus(Status.Active)).length,
            ),
          },
        };
      case Steps.ReceiveConfig:
        return {
          Component: ReceiveConfig,
          props: {
            options,
            isLoading,
            updateReceiveConfig,
            setIsLoading,
          },
        };
      case Steps.Photo:
        return {
          Component: CameraPhoto,
          props: {
            onPhoto: handlePhotoClick,
            lastPiece,
            onLastPieceClick: () => {
              setCaptureState(Steps.Slider);
              setCursor(lastPiece?.id);
            },
          },
        };

      case Steps.SliderPrint:
      case Steps.Slider:
        return {
          Component: PieceSlider,
          props: {
            pieces: Object.values(files),
            onChangeSlide: setCursor,
            cursor,
            onClose: () => {
              setCaptureState(Steps.Photo);
              setCursor(null);
            },
            onAnnotationClick: () => setDrawer(DrawerTypes.Barcode),
            workflow: currentSessionProps.params?.workflow,
            autoPrint: captureState === Steps.SliderPrint,
            drawer,
          },
        };
      case Steps.Crop:
        return {
          Component: ImageEditor,
          props: {
            imageBase64:
              currentFile?.editImage ||
              getPieceAsset(currentFile?.piece?.pieceAssets || [], PieceAssetSize.Large, PieceAssetType.Original)?.url,
            croppingActive: true,
            cropperRef: cropperRef,
            forceFetch: !currentFile?.editImage,
          },
        };
      case Steps.ReviewItems:
        return {
          Component: Review,
          props: {
            mapCapturePiece: getFilteredFilesByStatus(Status.Active, true),
            setDrawer: setDrawer,
            onCropClick: handleCrop,
            onItemClick: setCursor,
            retryFailedPieces: retryFailedPieces,
            cursor,
            currentSession: currentSessionProps,
            onDeletePiece,
            isDrawerActive: !!drawer,
          },
        };
      case Steps.Images:
        return {
          Component: UploadImages,
          props: {
            ref: uploadImagesRef,
            type: options.pieceType,
            forceUpdate,
            onUpload,
            setInvalid: (valid: boolean) => dispatch({ type: ActionTypes.SET_UPLOAD_ERROR, payload: valid }),
          },
        };
      case Steps.NotFound:
        return {
          Component: NotFound,
          props: { onStartReceive: handleErrorStartReceive },
        };
      case Steps.AutomatedCapture:
        return {
          Component: AutomatedCapture,
          props: {
            files: getFilteredFilesByStatus(Status.Draft),
            onStartAutomatedCapture: handleStartAutomatedCapture,
            onStopAutomatedCapture: handleStopAutomatedCapture,
            sessionId: paramSessionId,
            onDelete: onRemoveDrafts,
            isCaptureInProgress: automatedCaptureInProgress,
            onChooseCamera: handleSwitchAutomatedCamera,
            paperProps: { sx: { backgroundColor: 'background.default' } },
          },
        };
      case Steps.AutomatedCaptureCamera:
        return {
          Component: DeviceCamera,
          onClose: () => setCaptureState(Steps.AutomatedCapture),
        };
      default:
        return { Component: null, props: {} };
    }
  };

  const getDrawerContent = (): DrawerComponent => {
    switch (drawer) {
      case DrawerTypes.User:
        return {
          Component: AssignmentList,
          props: { file: currentFile, context: 'capture', onClose: closeDrawer, addEvent, darkMode: true },
        };
      case DrawerTypes.Location:
        return currentFile?.piece?.pieceLocation
          ? {
              Component: LocationID,
              props: {
                piece: currentFile?.piece,
                onChange: (piece: Piece) => setFileOption({ id: piece.id, option: 'piece', value: piece }),
                onClose: closeDrawer,
                context: 'capture',
              },
            }
          : { Component: null, props: { onClose: closeDrawer } };
      case DrawerTypes.Comments:
        return {
          Component: Comments,
          props: { pieceId: currentFile?.piece?.id, onClose: closeDrawer },
        };
      case DrawerTypes.AdditionalInfo:
        return {
          Component: AdditionalInfo,
          props: {
            pieceId: currentFile?.piece?.id,
            onClose: closeDrawer,
            annotations: currentFile?.pieceAnnotations || [],
            piece: currentFile?.piece,
            onCreate: (annotations: PieceAnnotation[]) =>
              dispatch({
                type: ActionTypes.MERGE_PIECE_ANNOTATIONS,
                payload: annotations,
              }),
          },
        };
      case DrawerTypes.Barcode:
        return {
          Component: Barcode,
          props: {
            onClose: closeDrawer,
            piece: currentFile?.piece,
            pieceAnnotations: currentFile?.pieceAnnotations || [],
            workflow: currentSessionProps.params?.workflow,
            detected: currentFile?.barcodeDetected,
            onCreate: (annotation: PieceAnnotation) =>
              dispatch({
                type: ActionTypes.MERGE_PIECE_ANNOTATIONS,
                payload: [annotation],
              }),
          },
        };
      default:
        return { Component: null, props: { onClose: closeDrawer } };
    }
  };

  return {
    captureState,
    getIconsByState,
    getComponentByState,
    onNextState,
    getButtonsByState,
    getTitleByState,
    getDrawerContent,
    setDrawer,
    isUnresolvedPieces,
    isUploadingPieces,
  };
};
